import * as React from 'react';
import { useEffect, useState } from 'react';
import { useCustomerStore } from '../../../redux/customers';
import { useAppSelector } from '../../../redux/hooks';
import { profilePackageByIdSelector, useProfilePackageStore } from '../../../redux/profile-packages';
import { EventDetail, EventAllocation } from '../../../models';
import { eventService } from '../../../services/event.service';
import { EventsPage } from '../components';

type EventsPageContainerProps = {
    eventId: string;
};

export const EventsPageContainer: React.FC<EventsPageContainerProps> = ({ eventId }) => {
    const customerStore = useCustomerStore();
    const { activeCustomer } = customerStore;
    const profilePackageStore = useProfilePackageStore();
    const { getById: getProfilePackageById, isFetching: isFetchingPackages } = profilePackageStore;

    const [event, setEvent] = useState<EventDetail | undefined>(undefined);
    const [allocations, setAllocations] = useState<EventAllocation[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!activeCustomer) return;

        setIsLoading(true);
        setErrorMessage(undefined);

        Promise.all([
            eventService.getEvent(eventId, activeCustomer.id),
            eventService.getMyAllocations(eventId, activeCustomer.id),
        ])
            .then(([eventResponse, allocationsResponse]) => {
                setEvent(eventResponse.results.event);
                setAllocations(allocationsResponse.results.allocations);
            })
            .catch((err) => {
                setErrorMessage(err?.errorMessage || 'Failed to load event.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [eventId, activeCustomer]);

    const manageBookingsProfilePackageId =
        allocations.find((a) => a.profilePackageId != null)?.profilePackageId ?? null;

    const manageBookingsProfilePackage = useAppSelector((state) =>
        profilePackageByIdSelector(state, manageBookingsProfilePackageId ?? undefined)
    );

    // A direct link into an event (e.g. from a push notification) can land
    // here before the home page has populated the profile-packages store.
    useEffect(() => {
        if (
            activeCustomer &&
            manageBookingsProfilePackageId != null &&
            !manageBookingsProfilePackage &&
            !isFetchingPackages
        ) {
            // unwrapResult rejects on a failed thunk; the store's errorMessage
            // already carries the failure, so there's nothing more to do here.
            getProfilePackageById({ id: manageBookingsProfilePackageId, customerId: activeCustomer.id }).catch(() => {});
        }
    }, [activeCustomer, manageBookingsProfilePackageId, manageBookingsProfilePackage, getProfilePackageById, isFetchingPackages]);

    return (
        <EventsPage
            event={event}
            allocations={allocations}
            isLoading={isLoading}
            errorMessage={errorMessage}
            manageBookingsProfilePackageId={manageBookingsProfilePackageId}
            canManageBookings={!!manageBookingsProfilePackage?.canManageBookings}
            canViewBookings={!!manageBookingsProfilePackage?.canViewBookings}
        />
    );
};
